import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { chatAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { 
    Video, 
    Calendar as CalendarIcon,
    Clock,
    Users,
    ExternalLink,
    Lock,
    Radio,
    MessageSquare,
    Loader2,
    Plus
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format, isSameDay, parseISO } from 'date-fns';

// Calendly link for scheduling
const CALENDLY_LINK = 'https://calendly.com/flywithshelly/book-one-on-one-zoom-private-live-session-60-m-clone';

export default function LiveClasses() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [showCreateSession, setShowCreateSession] = useState(false);
    const [sessionForm, setSessionForm] = useState({ 
        title: '', 
        scheduled_at: '', 
        description: '',
        google_meet_link: ''
    });
    const [creating, setCreating] = useState(false);

    // Check if user has membership access
    const hasMemberAccess = user && (
        user.membership_type === 'membership_basic' ||
        user.membership_type === 'membership_premier' ||
        user.membership_type === 'membership_premium' ||
        user.membership_type === 'membership_vip' ||
        user.is_admin
    );

    const isAdmin = user?.is_admin;

    useEffect(() => {
        loadSessions();
    }, []);

    const loadSessions = async () => {
        try {
            const res = await chatAPI.getSessions();
            setSessions(res.data || []);
        } catch (err) {
            console.error('Failed to load sessions:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSession = async (e) => {
        e.preventDefault();
        if (!sessionForm.title || !sessionForm.scheduled_at) {
            toast.error('Please fill in all required fields');
            return;
        }

        setCreating(true);
        try {
            await chatAPI.createSession({
                title: sessionForm.title,
                scheduled_at: sessionForm.scheduled_at,
                description: sessionForm.description,
                google_meet_link: sessionForm.google_meet_link
            });
            toast.success('Session created successfully!');
            setShowCreateSession(false);
            setSessionForm({ title: '', scheduled_at: '', description: '', google_meet_link: '' });
            loadSessions();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create session');
        } finally {
            setCreating(false);
        }
    };

    const joinSession = (session) => {
        if (!hasMemberAccess) {
            toast.error('Please purchase a membership to join live sessions');
            navigate('/membership');
            return;
        }

        if (session.google_meet_link) {
            window.open(session.google_meet_link, '_blank');
        } else {
            toast.error('No Google Meet link available for this session');
        }
    };

    // Get sessions for selected date
    const sessionsForDate = sessions.filter(session => {
        try {
            return isSameDay(parseISO(session.scheduled_at), selectedDate);
        } catch {
            return false;
        }
    });

    // Get dates with sessions for calendar highlighting
    const datesWithSessions = sessions.map(s => {
        try {
            return parseISO(s.scheduled_at);
        } catch {
            return null;
        }
    }).filter(Boolean);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen py-12 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div 
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <Badge className="mb-4 bg-primary/10 text-primary">Community</Badge>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                        Live Sessions
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        Join live group coaching sessions with Coach Shelly via Google Meet
                    </p>
                </motion.div>

                {/* Access Check */}
                {!hasMemberAccess && (
                    <Card className="p-6 mb-8 border-amber-500 bg-amber-50 dark:bg-amber-900/20">
                        <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                <Lock className="h-6 w-6 text-amber-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-amber-800 dark:text-amber-200">
                                    Membership Required
                                </h3>
                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                    Join a membership tier to access live group coaching sessions
                                </p>
                            </div>
                            <Button onClick={() => navigate('/membership')} className="rounded-full">
                                View Plans
                            </Button>
                        </div>
                    </Card>
                )}

                {/* Admin Create Session Button */}
                {isAdmin && (
                    <div className="flex justify-end mb-6">
                        <Button onClick={() => setShowCreateSession(true)} className="gap-2">
                            <Plus className="h-4 w-4" />
                            Create Live Session
                        </Button>
                    </div>
                )}

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Calendar */}
                    <Card className="p-6 lg:col-span-1">
                        <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                            <CalendarIcon className="h-5 w-5 text-primary" />
                            Schedule
                        </h2>
                        <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => date && setSelectedDate(date)}
                            className="rounded-md border"
                            modifiers={{
                                hasSession: datesWithSessions
                            }}
                            modifiersStyles={{
                                hasSession: {
                                    fontWeight: 'bold',
                                    backgroundColor: 'hsl(var(--primary) / 0.1)',
                                    color: 'hsl(var(--primary))'
                                }
                            }}
                        />
                        <p className="text-xs text-muted-foreground mt-4 text-center">
                            Highlighted dates have scheduled sessions
                        </p>
                    </Card>

                    {/* Sessions List */}
                    <div className="lg:col-span-2 space-y-6">
                        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                            <Video className="h-5 w-5 text-primary" />
                            Sessions for {format(selectedDate, 'MMMM d, yyyy')}
                        </h2>

                        {sessionsForDate.length === 0 ? (
                            <Card className="p-8 text-center">
                                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="font-semibold mb-2">No Sessions Scheduled</h3>
                                <p className="text-sm text-muted-foreground">
                                    No live sessions are scheduled for this date. Check back later!
                                </p>
                            </Card>
                        ) : (
                            <div className="space-y-4">
                                {sessionsForDate.map((session) => (
                                    <motion.div
                                        key={session.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        <Card className="p-6">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        {session.is_active && (
                                                            <Badge className="bg-red-500 text-white animate-pulse">
                                                                <Radio className="h-3 w-3 mr-1" />
                                                                LIVE NOW
                                                            </Badge>
                                                        )}
                                                        <h3 className="font-heading text-lg font-semibold">
                                                            {session.title}
                                                        </h3>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="h-4 w-4" />
                                                            {format(parseISO(session.scheduled_at), 'h:mm a')}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Users className="h-4 w-4" />
                                                            Group Session
                                                        </span>
                                                    </div>
                                                    {session.description && (
                                                        <p className="text-sm text-muted-foreground">
                                                            {session.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <Button 
                                                    onClick={() => joinSession(session)}
                                                    disabled={!hasMemberAccess}
                                                    className="rounded-full gap-2"
                                                    variant={session.is_active ? 'default' : 'outline'}
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                    {session.is_active ? 'Join Now' : 'Join Session'}
                                                </Button>
                                            </div>
                                        </Card>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        {/* Upcoming Sessions */}
                        <div className="mt-8">
                            <h2 className="font-heading text-lg font-semibold mb-4">All Upcoming Sessions</h2>
                            {sessions.length === 0 ? (
                                <p className="text-muted-foreground">No upcoming sessions scheduled.</p>
                            ) : (
                                <div className="space-y-3">
                                    {sessions
                                        .filter(s => new Date(s.scheduled_at) >= new Date())
                                        .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
                                        .slice(0, 5)
                                        .map((session) => (
                                            <Card key={session.id} className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="font-medium">{session.title}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {format(parseISO(session.scheduled_at), 'EEE, MMM d @ h:mm a')}
                                                        </p>
                                                    </div>
                                                    {session.is_active && (
                                                        <Badge className="bg-red-500 text-white">LIVE</Badge>
                                                    )}
                                                </div>
                                            </Card>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Create Session Modal (Admin Only) */}
                <Dialog open={showCreateSession} onOpenChange={setShowCreateSession}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Create Live Session</DialogTitle>
                            <DialogDescription>
                                Schedule a new Google Meet session for members
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateSession} className="space-y-4">
                            <div>
                                <Label>Session Title *</Label>
                                <Input
                                    value={sessionForm.title}
                                    onChange={(e) => setSessionForm({...sessionForm, title: e.target.value})}
                                    placeholder="e.g., Weekly Q&A Session"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Date & Time *</Label>
                                <Input
                                    type="datetime-local"
                                    value={sessionForm.scheduled_at}
                                    onChange={(e) => setSessionForm({...sessionForm, scheduled_at: e.target.value})}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Google Meet Link</Label>
                                <Input
                                    value={sessionForm.google_meet_link}
                                    onChange={(e) => setSessionForm({...sessionForm, google_meet_link: e.target.value})}
                                    placeholder="https://meet.google.com/xxx-xxxx-xxx"
                                    className="mt-1"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Create a Google Meet and paste the link here
                                </p>
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Textarea
                                    value={sessionForm.description}
                                    onChange={(e) => setSessionForm({...sessionForm, description: e.target.value})}
                                    placeholder="What will be covered in this session..."
                                    className="mt-1"
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={() => setShowCreateSession(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={creating} className="flex-1">
                                    {creating ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : null}
                                    Create Session
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}
